// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component } from '@angular/core';
import {Observable, of, Subject, throwError} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HeaderService } from '../../../header.service';
import {catchError, map, switchMap, withLatestFrom} from 'rxjs/operators';
import { LegendPosition, ScaleType } from '@swimlane/ngx-charts';
import {DashboardService, IBitLockerCompliance} from '../../../../services/dashboard.service';
import { TranslateService } from '@ngx-translate/core';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {AppState} from '../../../store/app.state';
import {selectUser} from '../../../store/user/user.selectors';
import {OrgType} from '../../../../types/group';
import { IManageUser } from '../../../../types/user';

interface ChartData {
  name: string;
  value: number;
}

interface CigentComplianceResult {
  response: IBitLockerCompliance;
  chartData: ChartData[];
}

@UntilDestroy({checkProperties: true})
@Component({
    selector: 'app-cigent-compliance',
    templateUrl: './cigent-compliance.component.html',
    styleUrls: ['./cigent-compliance.component.scss', '../dynamic-dashboard.common.scss'],
    standalone: false
})
export class CigentComplianceComponent {
  cigentComplianceResult$?: Observable<CigentComplianceResult> = new Subject<CigentComplianceResult>();
  graphStyle = '185px';
  // data = [{'name': 'Compliant', 'value': 0}, {'name': 'Non-Compliant', 'value': 0}];
  showLegend = true;
  showLabels = false;
  isDoughnut = true;
  legendPosition: LegendPosition = LegendPosition.Below;
  chooseCustomer = false;

  colorScheme = {
    name: 'cigent-compliance',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: [
      '#5AA454',
      '#A10A28',
    ]
  }

  constructor(private headerService: HeaderService,
              private dashboardService: DashboardService,
              private readonly store: Store<AppState>,
              private readonly router: Router,
              private translate: TranslateService) {
    this.cigentComplianceResult$ = this.headerService.orgSelectedChanged.pipe(
      withLatestFrom(this.store.select(selectUser())),
      switchMap(([selectedOrgId, user]: [string, IManageUser]) => {
        if (user.org_type.value !== OrgType.CUSTOMER && selectedOrgId.toLowerCase() === 'all') {
          this.chooseCustomer = true;
          const emptyResult: IBitLockerCompliance = {
            total: 0,
            compliant: 0,
            nonCompliant: 0,
            errors: 0,
          }
          return of(emptyResult);
        } else {
          this.chooseCustomer = false;
        }
        return this.dashboardService.getBitLockerCompliance((selectedOrgId.toLowerCase() === 'all') ? null : selectedOrgId);
      }),
      map((complianceResult: IBitLockerCompliance): CigentComplianceResult => {
        return {
          response: complianceResult,
          chartData: [
            {name: this.translate.instant('COMPLIANT'), value: complianceResult.compliant},
            {name: this.translate.instant('NON_COMPLIANT'), value: complianceResult.nonCompliant},
          ]
        };
      }),
      catchError(error => {
        return throwError(error);
      }),
      untilDestroyed(this)
    );
  }

  hasData(cigentComplianceResult: CigentComplianceResult) {
    return cigentComplianceResult?.response?.total > 0;
  }

  onClick(event) {
    this.router.navigate(['/endpoints'], {
      queryParams: {
        endpoints_target: 'encryption',
        cigent_compliance: event.label,
      }
    });
  }
}